import * as React from 'react';
import MuiSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { SelectChangeEvent } from '@mui/material/Select';

import { IntlMessage } from 'app/components/Intl';
import { isEqual, isNil } from 'app/utils';

import type { RegulationsField, RegulationsFieldItem } from './RegulationsField.types';
import { useRegulationsFieldValue } from './useRegulationsFieldValue';

export type RegulationsSelectFieldProps = {
  field: RegulationsField;
  className?: string;
  variant?: string;
  onChange?: (value: any) => void;
};

const EMPTY_ITEMS: Array<RegulationsFieldItem | string | number> = [];

const normalizeItems = (
  items: Array<RegulationsFieldItem | string | number> = EMPTY_ITEMS,
): Array<RegulationsFieldItem> =>
  items.map(item => (typeof item === 'object' && item !== null ? item : { element: `${item}`, value: item }));

const RegulationsSelectFieldComponent = ({ field, className, variant, onChange }: RegulationsSelectFieldProps) => {
  const {
    name,
    value,
    defaultValue,
    type = 'select',
    items = EMPTY_ITEMS,
    required = false,
    disabled = false,
    fullWidth = true,
  } = field;

  const normalizedItems = React.useMemo(() => normalizeItems(items), [items]);
  const defaultItem = React.useMemo(() => normalizedItems.find(item => item.default), [normalizedItems]);

  const initialValue = React.useMemo(() => {
    if (!isNil(value)) {
      return value;
    }
    if (!isNil(defaultValue)) {
      return defaultValue;
    }
    return defaultItem?.value ?? '';
  }, [value, defaultValue, defaultItem]);

  const [currentValue, commit] = useRegulationsFieldValue(name, initialValue);

  const size = variant === 'slim' ? 'small' : undefined;

  const handleCommit = React.useCallback(
    (nextValue: any) => {
      commit(nextValue);
      onChange?.(nextValue);
    },
    [commit, onChange],
  );

  const onSelectChange = React.useCallback(
    (event: SelectChangeEvent<any>) => {
      handleCommit(event.target.value);
    },
    [handleCommit],
  );

  if (type === 'autocomplete') {
    const selectedOption =
      normalizedItems.find(item => isEqual(item.value, currentValue)) ??
      (isNil(currentValue) || currentValue === '' ? null : { element: `${currentValue}`, value: currentValue });

    return (
      <Autocomplete
        className={className}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        options={normalizedItems}
        value={selectedOption}
        isOptionEqualToValue={(option, val) => isEqual(option?.value, val?.value)}
        getOptionLabel={option => (typeof option?.element === 'string' ? option.element : `${option?.value ?? ''}`)}
        onChange={(_event, option) => handleCommit(option?.value ?? null)}
        renderInput={params => <TextField {...params} required={required} />}
      />
    );
  }

  return (
    <FormControl className={className} size={size} fullWidth={fullWidth} required={required} disabled={disabled}>
      <MuiSelect value={currentValue ?? ''} onChange={onSelectChange}>
        {normalizedItems.map(item => (
          <MenuItem key={`${item.value}`} value={item.value} disabled={item.disabled}>
            <IntlMessage value={item.element ?? `${item.value}`} />
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
};

export const RegulationsSelectField = React.memo(RegulationsSelectFieldComponent);
